/**
 * Does a build stamp name the same build as another — and does a build CARRY
 * a given one?
 *
 * The deck never orders two builds: only the consumer's git knows what
 * contains what. What it CAN do is let the consumer answer that question
 * ahead of time. A qa-context `build` may list `contains` — the builds this
 * one is built on top of, as the consumer's git reported at image-build time
 * — and a fix claimed in any of them is in front of the tester. Without that
 * list the deck is back to equality, and reports the pair.
 *
 * Why this exists: twenty fixes were claimed against two staging builds, a
 * third build then went to staging carrying both, and every retest row warned
 * "this environment is running <other build> … you will still see the old
 * behaviour". Fifteen false warnings, each an invitation to a false "still
 * broken".
 *
 * Shas are matched by PREFIX when both sides look like hex and are long enough
 * to mean something: a fix command writes `git rev-parse --short` (7–12 chars)
 * where the probe reports the full 40, and those are the same build. Anything
 * else — a tag, a release number — must be equal outright, since `v1.2` is not
 * a prefix of `v1.2.3` in any sense that matters.
 */

/** Below this a hex prefix stops naming one commit (git's own default short). */
const MIN_SHA_PREFIX = 7;

function isHexSha(value: string): boolean {
  return value.length >= MIN_SHA_PREFIX && /^[0-9a-f]+$/i.test(value);
}

/** Whether two stamps name the same build. */
export function isSameBuild(a: string, b: string): boolean {
  if (a === b) return true;
  if (!isHexSha(a) || !isHexSha(b)) return false;
  const [short, long] = a.length <= b.length ? [a, b] : [b, a];
  return long.toLowerCase().startsWith(short.toLowerCase());
}

/**
 * Whether `build` is `sha`, or lists it among the builds it contains.
 *
 * `contains` is optional and BOUNDED on the consumer's side (a window of
 * recent builds, not the whole history), so a `false` here means "not known
 * to carry it" — never "known not to". The caller's wording has to hedge
 * exactly that far, and no further.
 */
export function buildCarries(
  build: { sha: string; contains?: readonly string[] | null },
  sha: string,
): boolean {
  if (isSameBuild(build.sha, sha)) return true;
  return (build.contains ?? []).some((c) => isSameBuild(c, sha));
}
